import React, { useState, useEffect } from "react";
import { FaCog, FaSync, FaExclamationCircle } from "react-icons/fa";
import { Alert } from "@mui/material";
import useAuth from "../../../Hooks/useAuth";
import usePermission from "../../../Hooks/usePermission";
import {
  useLazyGetSyncByFirmIDQuery as useDefaultGetSyncHook,
  useUpdateFirmSyncMutation as useDefaultUpdateSyncHook,
  useDeleteFirmProductMutation,
} from "../../../store/storageRoles/firmApi";
import { useGetTrustedDevicesQuery } from "../../../store";

import ProductEditForm from "./ProductEditForm";
import ProductCardSkeleton from "./ProductCardSkeleton";
import ProductCard from "./ProductCard";
import PasswordConfirmModal from "../../Utils/PasswordConfirmModal";
import { MascotLoadingOverlay } from "../../shared";

const ProductSettingsTab = ({
  firm,
  readOnly = false,
  dealerView = false,
  useGetSyncHook = useDefaultGetSyncHook,
  useUpdateSyncHook = useDefaultUpdateSyncHook,
}) => {
  const { auth } = useAuth();
  const { hasPermission, hasAnyPermission, isAdmin } = usePermission();
  const [syncData, setSyncData] = useState([]);
  const [quotaData, setQuotaData] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [alert, setAlert] = useState(null);
  // Ürün silme — SADECE admin (sistem yöneticisi); cihaz silmedeki
  // PasswordConfirmModal kalıbı: şifre + şifre onayı + (2FA açıksa) kod
  const [deleteModal, setDeleteModal] = useState({ open: false, product: null });

  const { data: trustedDevicesData } = useGetTrustedDevicesQuery(undefined, {
    skip: !auth?.accessToken || !isAdmin,
  });
  const twoFactorEnabled = trustedDevicesData?.data?.twoFactorEnabled === true;

  const [deleteFirmProduct, {
    isLoading: deleteProductLoading,
    isSuccess: deleteProductSuccess,
    isError: deleteProductIsError,
    error: deleteProductError,
    reset: resetDeleteProduct,
  }] = useDeleteFirmProductMutation();

  const [updateFirmSync, {
    isLoading: updateLoading,
    isSuccess: updateSuccess,
    isError: updateIsError,
    data: updateData,
    error: updateError,
    reset: resetUpdate,
  }] = useUpdateSyncHook();

  const [
    getSyncByFirmID,
    {
      data: syncDataResponse,
      isLoading: syncIsLoading,
      isFetching: syncIsFetching,
      isError: syncIsError,
    },
  ] = useGetSyncHook();

  useEffect(() => {
    if (syncDataResponse?.data) {
      setSyncData(syncDataResponse.data.productInfoes || []);
      setQuotaData(syncDataResponse.data.quotaArray || []);
    }
  }, [syncDataResponse?.data]);

  useEffect(() => {
    if (firm?._id && auth?.accessToken) {
      getSyncByFirmID({
        id: firm._id,
        token: auth.accessToken,
      });
    }
  }, []);

  useEffect(() => {
    if (alert) {
      setTimeout(() => setAlert(null), 5000);
    }
  }, [alert]);

  // Güncelleme sonucu
  useEffect(() => {
    if (updateSuccess) {
      // Alt firmalara uygulandıysa kaç firmanın güncellendiğini de söyle —
      // atlananlar (üründe o firma yok / ayar zaten aynı) ve hata alanlar
      // sessizce kaybolmasın.
      const sub = updateData?.data?.subFirms;
      let message = "Ürün ayarları başarıyla güncellendi!";
      if (sub) {
        const parts = [`${sub.applied?.length || 0} alt firmaya uygulandı`];
        if (sub.skipped?.length) parts.push(`${sub.skipped.length} atlandı`);
        if (sub.failed?.length) parts.push(`${sub.failed.length} başarısız`);
        message += ` (${parts.join(", ")})`;
      }
      setAlert({
        severity: sub?.failed?.length ? "warning" : "success",
        message,
      });
      setShowEditModal(false);
      setSelectedProduct(null);
      if (updateData?.data?.productInfoes) {
        setSyncData(updateData.data.productInfoes);
      }
      if (updateData?.data?.quotaArray) {
        setQuotaData(updateData.data.quotaArray);
      }
      resetUpdate();
    }
  }, [updateSuccess]);

  useEffect(() => {
    if (updateIsError) {
      setAlert({
        severity: "error",
        message: updateError?.data?.message || "Güncelleme sırasında hata oluştu",
      });
      setShowEditModal(false);
      setSelectedProduct(null);
      resetUpdate();
    }
  }, [updateIsError]);

  const handleRefresh = () => {
    if (firm?._id && auth?.accessToken) {
      getSyncByFirmID({ id: firm._id, token: auth.accessToken });
    }
  };

  // Ürün silme sonucu
  useEffect(() => {
    if (deleteProductSuccess) {
      setAlert({ severity: "success", message: "Ürün firmadan silindi." });
      setDeleteModal({ open: false, product: null });
      resetDeleteProduct();
      handleRefresh();
    }
    if (deleteProductIsError) {
      setAlert({
        severity: "error",
        message: deleteProductError?.data?.message || "Ürün silinirken hata oluştu.",
      });
      resetDeleteProduct();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteProductSuccess, deleteProductIsError]);

  const handleDeleteProduct = (product) => {
    setDeleteModal({ open: true, product });
  };

  const handleDeleteProductConfirm = ({ password, passwordConfirm, code }) => {
    if (!deleteModal.product) return;
    deleteFirmProduct({
      id: firm._id,
      productName: deleteModal.product.productName,
      password,
      passwordConfirm,
      ...(twoFactorEnabled && code ? { code } : {}),
      token: auth.accessToken,
    });
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setShowEditModal(true);
  };

  const handleFormSubmit = (formData) => {
    const updateData = {
      ...formData,
      // Alt firmalara yayılım — server her alt firmanın AYNI ürününü ve
      // cihazlarını aynı mantıkla günceller (bakiye kopyalanmaz).
      applyToSubFirms: !!formData.applyToSubFirms,
      productPrice: parseFloat(formData.productPrice),
      quotaMax: parseInt(formData.quotaMax),
      quotaWarning: parseInt(formData.quotaWarning),
      syncLevel: parseInt(formData.syncLevel),
      quotaCoefficient: parseInt(formData.quotaCoefficient),
      orderFrequencyDays: parseInt(formData.orderFrequencyDays),
      goalConsumentMonthly: parseInt(formData.goalConsumentMonthly),
      id: firm._id,
      token: auth.accessToken,
    };
    updateFirmSync(updateData);
  };

  return (
    <div className="flex flex-col h-full w-full">
      {/* Alert */}
      {alert && (
        <div className="fixed z-50 left-1/2 top-4 -translate-x-1/2 w-[90%] max-w-md">
          <Alert
            severity={alert.severity}
            onClose={() => setAlert(null)}
            variant="filled"
            sx={{ boxShadow: "0 4px 12px rgba(0,0,0,0.15)", width: "100%", borderRadius: "12px", fontSize: "13px" }}
          >
            {alert.message}
          </Alert>
        </div>
      )}

      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm border-b border-gray-100 shrink-0 px-2 sm:px-4 py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 bg-purple-100 rounded-lg">
              <FaCog className="w-3.5 h-3.5 text-purple-600" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-gray-800 leading-tight">
                Ürün Ayarları
              </h2>
              <p className="text-[11px] sm:text-xs text-gray-500">
                {syncData.length} ürün konfigürasyonu
              </p>
            </div>
          </div>

          <button
            onClick={handleRefresh}
            disabled={syncIsLoading || syncIsFetching}
            className="flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 bg-gray-100 text-gray-600 hover:bg-emerald-50 hover:text-emerald-600 rounded-lg text-xs font-medium transition-all disabled:opacity-40"
          >
            <FaSync className={`w-3 h-3 ${syncIsLoading || syncIsFetching ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Yenile</span>
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0 px-2 sm:px-4 py-3">
        {syncIsLoading || syncIsFetching ? (
          <div className="relative">
            <MascotLoadingOverlay message="Ürün ayarları yükleniyor..." />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
              {[...Array(4)].map((_, index) => (
                <ProductCardSkeleton key={index} />
              ))}
            </div>
          </div>
        ) : syncIsError || syncData.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-3">
              <FaCog className="w-5 h-5 text-gray-400" />
            </div>
            <h3 className="text-sm font-semibold text-gray-800 mb-1">Ürün Ayarları Bulunamadı</h3>
            <p className="text-xs text-gray-500">Bu firmaya ait ürün ayarları bulunmamaktadır.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
            {syncData.map((product, index) => (
              <ProductCard
                key={index}
                product={product}
                quotaData={quotaData}
                onEdit={readOnly || !hasAnyPermission("firms.editProductSettings", "firms.editProductQuotaDistribution", "firms.editProductQuotaWarning") ? undefined : handleEdit}
                onDelete={!readOnly && isAdmin ? handleDeleteProduct : undefined}
                isLoading={updateLoading}
                dealerView={dealerView}
              />
            ))}
          </div>
        )}
      </div>

      {/* Düzenleme Formu Modal */}
      {showEditModal && selectedProduct && (
        <ProductEditForm
          product={selectedProduct}
          onSave={handleFormSubmit}
          dealerView={dealerView}
          onClose={() => {
            setShowEditModal(false);
            setSelectedProduct(null);
          }}
          isLoading={updateLoading}
        />
      )}

      {/* Ürün Silme Onayı — cihaz silmedeki PasswordConfirmModal kalıbı:
          şifre + şifre onayı + (2FA açıksa) 6 haneli kod */}
      <PasswordConfirmModal
        open={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, product: null })}
        onConfirm={handleDeleteProductConfirm}
        title="Ürün Silme İşlemi"
        subtitle={`"${deleteModal.product?.productName}" ürünü firmanın ürün listesinden tamamen silinecek. Bu işlem geri alınamaz; ürün aynı adla bir cihazda hâlâ tanımlıysa senkronda firmaya yeniden eklenebilir. Lütfen şifrenizi girerek onaylayın.${twoFactorEnabled ? " İki adımlı doğrulama kodu da gereklidir." : ""}`}
        confirmButtonText="Ürünü Sil"
        isLoading={deleteProductLoading}
        twoFactorEnabled={twoFactorEnabled}
      />
    </div>
  );
};

export default ProductSettingsTab;
